import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { Dispatch, SetStateAction } from 'react';
import { getRouteDisplayName } from '@/components/Bookmarks/BookmarkModal/utils/parseRouteName';
import type { Bookmark } from '@/types/bookmarks';
import { now } from '@/utils/dates';
import { factory } from '@/utils/queryFactory';
import { BOOKMARKS_QUERY_KEY } from '@/utils/queryFactory/resources/bookmarks';
import { cn } from '@/utils/ui-utils';
import { ActionButtons } from './ActionButtons';
import { EditInput } from './EditInput';

interface BookmarkCard {
  bookmark: Bookmark;
  editId: number | null;
  setEditId: Dispatch<SetStateAction<number | null>>;
  isActive?: boolean;
}

export const BookmarkCard = ({ bookmark, editId, setEditId, isActive = false }: BookmarkCard) => {
  const queryClient = useQueryClient();

  const updateBookmark = useMutation({
    ...factory.bookmarks.updateBookmark({ bookmarkId: bookmark.id }),
    async onSuccess() {
      const res = await queryClient.invalidateQueries({ queryKey: [BOOKMARKS_QUERY_KEY] });
      setEditId(null);

      return res;
    },
  });

  const handleClickBookmark = () => {
    if (editId) return;

    updateBookmark.mutate({
      ...bookmark,
      last_viewed: now().toISOString(),
      viewed_count: bookmark.viewed_count + 1,
    });

    window.location.href = bookmark.currentURL;
  };
  return (
    <div className='p-1'>
      {/* biome-ignore lint/a11y/useSemanticElements: card contains interactive ActionButtons, so it can't be a <button> (invalid nested interactive); role=button + keyboard handler is the accessible compromise */}
      <div
        className={cn(
          'hover:bg-accent relative w-full cursor-pointer space-y-4 rounded-lg border p-2',
          `focus-visible:ring-ring ring-offset-background focus-visible:ring-2 focus-visible:ring-offset-1
          focus-visible:outline-hidden`,
        )}
        tabIndex={0}
        role='button'
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            const target = e.target as HTMLElement;
            if (target === e.currentTarget) {
              e.preventDefault();
              handleClickBookmark();
            }
          }
        }}
        onClick={() => handleClickBookmark()}
      >
        <div className='tablet-lg:flex-wrap flex items-center justify-between gap-2'>
          {editId === bookmark.id ? (
            <EditInput
              bookmark={bookmark}
              setEditId={setEditId}
              updateBookmark={updateBookmark}
            />
          ) : (
            <h3 className={cn('text-md font-semibold', isActive && 'text-highlight')}>{bookmark.name}</h3>
          )}
          <ActionButtons
            bookmark={bookmark}
            setEditId={setEditId}
          />
        </div>
        <p className='w-full text-xs'>{bookmark.searchLabel}</p>
        <div className='font-display flex gap-2 text-xs text-black'>
          <span className='bg-accent rounded-full border px-3 py-1'>
            {getRouteDisplayName(bookmark.currentRouteName)}
          </span>
          <span
            className='bg-accent inline-block max-w-48 overflow-hidden rounded-full border px-3 py-1 text-nowrap
              text-ellipsis whitespace-nowrap'
          >
            {bookmark.areaDescription}
          </span>
        </div>
      </div>
    </div>
  );
};
